import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  IconButton,
  VStack,
  HStack,
  Text,
  Grid,
  Button,
  Spinner,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { Plus, Search, X } from "lucide-react";
import {
  createSoaSchema,
  type CreateSoaFormData,
} from "@/lib/validations/auth";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { Pagination } from "@/components/tables/Pagination";
import type { SoaLineItemsMeta } from "@/components/tables/types";
import {
  buildSoaDisplayColumns,
  getSoaCellValue,
  getSoaColumnKey,
  getSoaFooterTotalForColumn,
  isSoaNumericColumn,
  sumSoaAmountColumn,
  type SoaDisplayColumn,
} from "@/lib/soa/soaTableColumns";

interface InvalidBookingEntry {
  booking?: { id?: number; reference_number?: string };
  reason?: string;
}

const NO_BOOKINGS_TOAST_ID = "statement-of-account-form-no-bookings";
const AVAILABLE_BOOKINGS_PER_PAGE = 50;

const formatCurrency = (value: number): string =>
  `P ${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface StatementOfAccountFormProps {
  shippingLineId: string;
  shippingLineName: string;
  selectedBookingIds: number[];
  soaId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export function StatementOfAccountForm({
  shippingLineId,
  shippingLineName,
  selectedBookingIds,
  soaId,
  onSuccess,
  onCancel,
}: StatementOfAccountFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess, toast } = useToastNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [lineItemsData, setLineItemsData] = useState<Record<string, unknown>[]>(
    [],
  );
  const [lineItemColumns, setLineItemColumns] = useState<SoaDisplayColumn[]>([]);
  const [lineItemsMeta, setLineItemsMeta] = useState<SoaLineItemsMeta | null>(null);
  const [finalBookingIds, setFinalBookingIds] =
    useState<number[]>(selectedBookingIds);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const isEditMode = !!soaId;
  const isMountedRef = useRef(true);
  const noBookingsWarningShownRef = useRef(false);
  const [originalBookingIds, setOriginalBookingIds] = useState<number[]>([]);
  const [addedBookingIds, setAddedBookingIds] = useState<number[]>([]);
  const [availableBookings, setAvailableBookings] = useState<any[]>([]);
  const [selectedBookingToAdd, setSelectedBookingToAdd] = useState<string>("");
  const [isLoadingAvailableBookings, setIsLoadingAvailableBookings] =
    useState(false);
  const [isValidatingAddBooking, setIsValidatingAddBooking] = useState(false);
  const [invalidBookingsModalOpen, setInvalidBookingsModalOpen] =
    useState(false);
  const [invalidBookings, setInvalidBookings] = useState<InvalidBookingEntry[]>(
    [],
  );
  const [bookingDateFrom, setBookingDateFrom] = useState("");
  const [bookingDateTo, setBookingDateTo] = useState("");
  const [bookingReferenceSearch, setBookingReferenceSearch] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateSoaFormData>({
    resolver: zodResolver(createSoaSchema),
    defaultValues: {
      dli_sa_number: "",
      work_order: "",
    },
  });

  const effectiveBookingIdsLength =
    finalBookingIds.length > 0 ? finalBookingIds.length : selectedBookingIds.length;

  useEffect(() => {
    if (isEditMode) return;
    if (effectiveBookingIdsLength > 0) return;
    if (noBookingsWarningShownRef.current) return;
    noBookingsWarningShownRef.current = true;
    if (toast.isActive(NO_BOOKINGS_TOAST_ID)) return;
    toast({
      id: NO_BOOKINGS_TOAST_ID,
      title: "Please select at least one booking",
      description:
        "Go back to Financial Statements, select one or more bookings, then use Generate SOA.",
      status: "warning",
      position: "top-right",
      duration: 5000,
      isClosable: true,
    });
  }, [isEditMode, effectiveBookingIdsLength, toast]);

  const canSubmitSoa = isEditMode || effectiveBookingIdsLength > 0;

  const amountGrandTotal = useMemo(() => {
    if (lineItemsMeta?.total_amount != null) {
      return lineItemsMeta.total_amount;
    }
    const isClientSidePagination =
      lineItemsMeta != null &&
      lineItemsData.length >= lineItemsMeta.total &&
      lineItemsMeta.total > 0;
    if (isClientSidePagination) {
      return sumSoaAmountColumn(lineItemsData, lineItemColumns);
    }
    return null;
  }, [lineItemsMeta, lineItemsData, lineItemColumns]);

  const hasTableTotals =
    lineItemsMeta != null &&
    (lineItemsMeta.total_amount != null ||
      lineItemsMeta.total_vat != null ||
      lineItemsMeta.grand_total != null);

  // Fetch SOA data when in edit mode
  useEffect(() => {
    if (!isEditMode || !soaId) return;
    isMountedRef.current = true;
    const fetchSoaData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/soa/${soaId}`);
        if (!isMountedRef.current) return;
        const soaData = response.data?.data || response.data || {};

        reset({
          dli_sa_number: soaData.dli_sa_number || "",
          work_order: soaData.work_order || "",
        });

        // Extract booking IDs from SOA data
        const bookingIds = soaData.booking_ids || [];
        setFinalBookingIds(
          bookingIds.length > 0 ? bookingIds : selectedBookingIds,
        );
        setOriginalBookingIds(
          bookingIds.length > 0 ? bookingIds : selectedBookingIds,
        );
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchSoaData();
    return () => {
      isMountedRef.current = false;
    };
  }, [isEditMode, soaId, reset, showError, selectedBookingIds]);

  const fetchAvailableBookings = useCallback(async () => {
    if (!isEditMode || !shippingLineId) return;

    if (
      bookingDateFrom &&
      bookingDateTo &&
      bookingDateFrom > bookingDateTo
    ) {
      showError("From date must be on or before To date");
      return;
    }

    setIsLoadingAvailableBookings(true);
    try {
      const params: Record<string, string | number> = {
        is_complete: 0,
        per_page: AVAILABLE_BOOKINGS_PER_PAGE,
      };
      if (bookingDateFrom) params.expected_date_from = bookingDateFrom;
      if (bookingDateTo) params.expected_date_to = bookingDateTo;
      const trimmedSearch = bookingReferenceSearch.trim();
      if (trimmedSearch) params.search = trimmedSearch;
      const response = await apiClient.get(
        `/bookings/by-shipping-line/${shippingLineId}`,
        { params },
      );
      if (!isMountedRef.current) return;

      const bookingsData = response.data?.data ?? [];
      setAvailableBookings(Array.isArray(bookingsData) ? bookingsData : []);
      setSelectedBookingToAdd("");
    } catch (error: unknown) {
      if (isMountedRef.current) {
        showError(error);
        setAvailableBookings([]);
      }
    } finally {
      if (isMountedRef.current) setIsLoadingAvailableBookings(false);
    }
  }, [
    isEditMode,
    shippingLineId,
    bookingDateFrom,
    bookingDateTo,
    bookingReferenceSearch,
    showError,
  ]);

  // Fetch line items from /soa/line-items (server paginated)
  useEffect(() => {
    let cancelled = false;
    const bookingIdsToUse =
      finalBookingIds.length > 0 ? finalBookingIds : selectedBookingIds;
    if (bookingIdsToUse.length === 0 || !shippingLineId) {
      setLineItemsData([]);
      setLineItemColumns([]);
      setLineItemsMeta(null);
      return () => {
        cancelled = true;
      };
    }

    const fetchLineItems = async () => {
      setIsLoadingBookings(true);
      try {
        const response = await apiClient.get("/soa/line-items", {
          params: {
            shipping_line_id: shippingLineId,
            booking_ids: bookingIdsToUse,
            page: currentPage,
            per_page: itemsPerPage,
          },
        });

        if (cancelled) return;
        const data = response.data?.data ?? [];
        const columns = response.data?.columns ?? [];
        const meta = response.data?.meta ?? null;
        setLineItemsData(Array.isArray(data) ? data : []);
        setLineItemColumns(
          buildSoaDisplayColumns(Array.isArray(columns) ? columns : []),
        );
        setLineItemsMeta(meta);
      } catch (error: unknown) {
        if (!cancelled) {
          showError(error);
          setLineItemsData([]);
          setLineItemColumns([]);
          setLineItemsMeta(null);
        }
      } finally {
        if (!cancelled) setIsLoadingBookings(false);
      }
    };

    fetchLineItems();
    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    finalBookingIds,
    itemsPerPage,
    selectedBookingIds,
    shippingLineId,
    showError,
  ]);

  // Format cell value: amount-like columns get "P " prefix when numeric or numeric string
  const formatCellValue = (value: unknown, columnName: string): string => {
    if (value == null || value === "") return "-";
    const name = String(columnName).toLowerCase();
    const isAmountColumn =
      name.includes("amount") || name.includes("vat") || name === "total";
    if (isAmountColumn && typeof value === "number") {
      return formatCurrency(value);
    }
    if (isAmountColumn && typeof value === "string" && value !== "") {
      return `P ${value}`;
    }
    return String(value);
  };

  // Reset to page 1 when the booking set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [finalBookingIds, selectedBookingIds, shippingLineId]);

  const handlePaginationClick = (url: string | null) => {
    if (!url) return;

    const pageMatch = url.match(/[?&]page=(\d+)/);
    const nextPage = pageMatch ? Number.parseInt(pageMatch[1], 10) : 1;

    setCurrentPage(Math.max(nextPage, 1));
  };

  const applyAddBooking = (bookingId: number) => {
    const updatedBookingIds = [...finalBookingIds, bookingId];
    setFinalBookingIds(updatedBookingIds);
    setAddedBookingIds([...addedBookingIds, bookingId]);
    setSelectedBookingToAdd("");
  };

  const handleAddBooking = async () => {
    if (!selectedBookingToAdd) return;

    const bookingId = parseInt(selectedBookingToAdd, 10);
    if (Number.isNaN(bookingId)) return;

    if (finalBookingIds.includes(bookingId)) {
      showError("This booking is already added");
      return;
    }

    setIsValidatingAddBooking(true);
    try {
      const response = await apiClient.post("/soa-billing-check/validate", {
        booking_ids: [bookingId],
        type: 1,
      });

      const payload = response.data ?? {};
      const invalidList: InvalidBookingEntry[] = Array.isArray(
        payload.invalid_bookings,
      )
        ? payload.invalid_bookings
        : [];

      if (invalidList.length > 0) {
        setInvalidBookings(invalidList);
        setInvalidBookingsModalOpen(true);
        return;
      }

      applyAddBooking(bookingId);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { invalid_bookings?: InvalidBookingEntry[] } };
      };
      const invalidFromError = err.response?.data?.invalid_bookings;
      if (Array.isArray(invalidFromError) && invalidFromError.length > 0) {
        setInvalidBookings(invalidFromError);
        setInvalidBookingsModalOpen(true);
        return;
      }
      showError(error);
    } finally {
      setIsValidatingAddBooking(false);
    }
  };

  const handleCancelWithReset = () => {
    // Reset to original booking IDs
    setFinalBookingIds(originalBookingIds);
    setAddedBookingIds([]);
    setSelectedBookingToAdd("");
    setAvailableBookings([]);
    setBookingReferenceSearch("");
    setInvalidBookingsModalOpen(false);
    setInvalidBookings([]);
    onCancel();
  };

  const onSubmit = async (data: CreateSoaFormData) => {
    // Validate booking_ids separately since it's not a form field
    const bookingIdsToUse =
      finalBookingIds.length > 0 ? finalBookingIds : selectedBookingIds;
    if (!bookingIdsToUse || bookingIdsToUse.length === 0) {
      showError("At least one booking is required");
      return;
    }

    if (!shippingLineId) {
      showError("Shipping line ID is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        shipping_line_id: shippingLineId,
        dli_sa_number: data.dli_sa_number.trim(),
        booking_ids: bookingIdsToUse,
        work_order: data.work_order?.trim() || null,
      };

      if (isEditMode && soaId) {
        await apiClient.put(`/soa/${soaId}`, payload);
        showSuccess("Success", "SOA updated successfully");
      } else {
        await apiClient.post("/soa/generate", payload);
        showSuccess("Success", "SOA created successfully");
      }

      onSuccess?.();
    } catch (error: any) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading SOA data...
        </Text>
      </Box>
    );
  }

  return (
    <Box minW={0} w="100%" maxW="100%">
      <VStack spacing={4} align="stretch">
        {/* Form Section */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
          <CardBody>
            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4} align="stretch">
              {/* Shipping Line Field (Read-only) */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium">
                  Shipping Line: {" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <FormControl>
                  <Input
                    value={shippingLineName}
                    readOnly
                    bg={colors.sectionBg}
                    borderColor={colors.border}
                  />
                </FormControl>
              </Grid>

              {/* DLI-SA Number Field */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium">
                  DLI-SA Number: {" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.dli_sa_number}>
                  <Input
                    {...register("dli_sa_number")}
                    placeholder="Please input DLI-SA no"
                    borderColor={colors.border}
                  />
                  <FormErrorMessage>
                    {errors.dli_sa_number?.message}
                  </FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Work Order Number Field */}
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <FormLabel pt={2} fontWeight="medium">
                  Work Order Number:
                </FormLabel>
                <FormControl isInvalid={!!errors.work_order}>
                  <Input
                    {...register("work_order")}
                    placeholder="Please input work order no (optional)"
                    borderColor={colors.border}
                  />
                  <FormErrorMessage>
                    {errors.work_order?.message}
                  </FormErrorMessage>
                </FormControl>
              </Grid>

              {/* Add Booking/s Field - Only in Edit Mode */}
              {isEditMode && (
                <Grid
                  templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                  gap={4}
                  alignItems="start"
                >
                  <FormLabel pt={2} fontWeight="medium">
                    Add Booking/s:
                  </FormLabel>
                  <VStack spacing={3} align="stretch">
                    <HStack spacing={2} flexWrap="wrap">
                      <InputGroup size="sm" maxW="220px" minW="150px">
                        <InputLeftAddon fontSize="sm">From</InputLeftAddon>
                        <Input
                          type="date"
                          value={bookingDateFrom}
                          onChange={(e) => setBookingDateFrom(e.target.value)}
                          borderColor={colors.border}
                        />
                      </InputGroup>
                      <InputGroup size="sm" maxW="220px" minW="150px">
                        <InputLeftAddon fontSize="sm">To</InputLeftAddon>
                        <Input
                          type="date"
                          value={bookingDateTo}
                          onChange={(e) => setBookingDateTo(e.target.value)}
                          borderColor={colors.border}
                        />
                      </InputGroup>
                      <InputGroup size="sm" maxW="220px" minW="150px">
                        <Input
                          value={bookingReferenceSearch}
                          onChange={(e) =>
                            setBookingReferenceSearch(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void fetchAvailableBookings();
                            }
                          }}
                          placeholder="Reference number"
                          borderColor={colors.border}
                          pr={
                            bookingReferenceSearch.trim().length > 0
                              ? "2.5rem"
                              : undefined
                          }
                        />
                        {bookingReferenceSearch.trim().length > 0 && (
                          <InputRightElement width="2.5rem">
                            <IconButton
                              aria-label="Clear reference number"
                              icon={<Icon as={X} boxSize={3.5} />}
                              size="xs"
                              variant="ghost"
                              onClick={() => setBookingReferenceSearch("")}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <Button
                        size="sm"
                        leftIcon={<Icon as={Search} boxSize={4} />}
                        onClick={() => void fetchAvailableBookings()}
                        colorScheme="blue"
                        variant="outline"
                        isLoading={isLoadingAvailableBookings}
                        loadingText="Searching..."
                      >
                        Search
                      </Button>
                    </HStack>
                    <HStack spacing={2}>
                      <FormControl flex={1}>
                        <Select
                          value={selectedBookingToAdd}
                          onChange={(e) =>
                            setSelectedBookingToAdd(e.target.value)
                          }
                          placeholder="Select booking to add"
                          borderColor={colors.border}
                          isDisabled={isLoadingAvailableBookings}
                        >
                          {availableBookings
                            .filter(
                              (booking) =>
                                !finalBookingIds.includes(booking.id),
                            )
                            .map((booking) => (
                              <option key={booking.id} value={booking.id}>
                                {booking.reference_number}
                              </option>
                            ))}
                        </Select>
                      </FormControl>
                      <Button
                        leftIcon={<Icon as={Plus} boxSize={4} />}
                        onClick={() => void handleAddBooking()}
                        colorScheme="blue"
                        isDisabled={
                          !selectedBookingToAdd ||
                          isLoadingAvailableBookings ||
                          isValidatingAddBooking
                        }
                        isLoading={isValidatingAddBooking}
                        loadingText="Validating..."
                      >
                        Add
                      </Button>
                    </HStack>
                  </VStack>
                </Grid>
              )}
            </VStack>
            </Box>
          </CardBody>
        </Card>

        {/* Table Section: scroll horizontally when wide, keep sidebar visible */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border} minW={0}>
          <CardBody p={0} minW={0}>
            {isLoadingBookings ? (
            <Box p={8} textAlign="center">
              <Spinner size="lg" color="blue.500" />
              <Text mt={4} color={colors.text}>
                Loading details...
              </Text>
            </Box>
            ) : lineItemsData.length === 0 ? (
              <Box p={8} textAlign="center">
                <Text color={colors.textSecondary}>
                  {isEditMode ? "No records found" : "No bookings selected"}
                </Text>
              </Box>
            ) : (
              <Box minW={0} overflowX="auto">
                <TableContainer minW="min-content">
                  <Table variant="simple" size="sm">
                    <Thead bg={colors.sectionBg}>
                      <Tr>
                        {lineItemColumns.map((col, index) => {
                          const isNumeric = isSoaNumericColumn(col.name);
                          return (
                            <Th
                              key={getSoaColumnKey(col, index)}
                              borderColor={colors.border}
                              color={colors.text}
                              isNumeric={isNumeric}
                            >
                              {col.name}
                            </Th>
                          );
                        })}
                      </Tr>
                    </Thead>
                  <Tbody>
                    {lineItemsData.map((row, index) => (
                      <Tr key={index}>
                        {lineItemColumns.map((col, colIndex) => {
                          const value = getSoaCellValue(row, col);
                          const isNumeric = isSoaNumericColumn(col.name);
                          return (
                            <Td
                              key={getSoaColumnKey(col, colIndex)}
                              borderColor={colors.border}
                              color={colors.text}
                              isNumeric={isNumeric}
                            >
                              {formatCellValue(value, col.name)}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                  {hasTableTotals && (
                    <Tfoot>
                      <Tr bg={colors.sectionBg} fontWeight="semibold">
                        {lineItemColumns.map((col, index) => {
                          const footerTotal = getSoaFooterTotalForColumn(
                            col.name,
                            lineItemsMeta,
                          );
                          const isNumeric = isSoaNumericColumn(col.name);
                          return (
                            <Td
                              key={getSoaColumnKey(col, index)}
                              borderColor={colors.border}
                              color={colors.text}
                              isNumeric={isNumeric}
                            >
                              {index === 0
                                ? "TOTAL"
                                : footerTotal != null
                                  ? formatCurrency(footerTotal)
                                  : "—"}
                            </Td>
                          );
                        })}
                      </Tr>
                    </Tfoot>
                  )}
                </Table>
                </TableContainer>
              </Box>
            )}
            {amountGrandTotal != null && lineItemsData.length > 0 && (
              <Box
                px={4}
                py={3}
                borderTopWidth="1px"
                borderColor={colors.border}
                bg={colors.sectionBg}
              >
                <HStack justify="flex-end" spacing={6} flexWrap="wrap">
                  <Text fontWeight="semibold" color={colors.text}>
                    Amount Total:{" "}
                    <Text as="span" fontWeight="bold">
                      {formatCurrency(amountGrandTotal)}
                    </Text>
                  </Text>
                  {lineItemsMeta?.total_vat != null && (
                    <Text fontWeight="semibold" color={colors.text}>
                      VAT Total:{" "}
                      <Text as="span" fontWeight="bold">
                        {formatCurrency(lineItemsMeta.total_vat)}
                      </Text>
                    </Text>
                  )}
                  {lineItemsMeta?.grand_total != null && (
                    <Text fontWeight="semibold" color={colors.text}>
                      Grand Total:{" "}
                      <Text as="span" fontWeight="bold">
                        {formatCurrency(lineItemsMeta.grand_total)}
                      </Text>
                    </Text>
                  )}
                </HStack>
              </Box>
            )}
            {/* Pagination Controls */}
            {lineItemsMeta && lineItemsData.length > 0 && (
              <Box
                p={4}
                borderTopWidth="1px"
                borderColor={colors.border}
              >
                <Pagination metas={lineItemsMeta} onClick={handlePaginationClick} />
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Invalid bookings modal */}
        <Modal
          isOpen={invalidBookingsModalOpen}
          onClose={() => setInvalidBookingsModalOpen(false)}
          isCentered
          size="md"
        >
          <ModalOverlay />
          <ModalContent bg={colors.cardBg} borderColor={colors.border}>
            <ModalHeader color={colors.text}>Invalid Bookings</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text color={colors.textSecondary} mb={3}>
                The following booking(s) cannot be added:
              </Text>
              <UnorderedList spacing={2} color={colors.text}>
                {invalidBookings.map((item, idx) => {
                  const ref =
                    item.booking?.reference_number ??
                    (item.booking?.id != null
                      ? `Booking #${item.booking.id}`
                      : null);
                  return (
                    <ListItem key={item.booking?.id ?? idx}>
                      {ref ?? "Booking"}
                      {item.reason && (
                        <Text as="span" color={colors.textSecondary} ml={1}>
                          — {item.reason}
                        </Text>
                      )}
                    </ListItem>
                  );
                })}
              </UnorderedList>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={() => setInvalidBookingsModalOpen(false)}
              >
                OK
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={2}>
          <Button
            variant="outline"
            onClick={handleCancelWithReset}
            borderColor={colors.border}
            color={colors.text}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            colorScheme="blue"
            isLoading={isSubmitting}
            isDisabled={isSubmitting || !canSubmitSoa}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
          >
            {isEditMode ? "Update SOA" : "Create SOA"}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
